"use client";

import { useImageLoading } from "../../../../helpers/ImageLoadingContext";
import { responsiveImageHelper } from "../../../../helpers/responsiveImageHelper";
import { Stage } from "../../../Stage/Stage";
import { QaStageDropdown } from "./QaStageDropdown";
import {
  CosmosButton,
  CosmosIconChevronRight,
  CosmosIconReload,
  CosmosIconSearch,
  CosmosText,
  CosmosTitle,
} from "@cosmos/web-react";
import classNames from "classnames/bind";
import { useSearchParams } from "next/navigation";
import { useRef, useState } from "react";
import styles from "./QaStage.module.css";

const cx = classNames.bind(styles);

type QaStageCategory = {
  id: string;
  name: string | null;
  title: string | null;
  linkText: string | null;
  linkUrl: string | null;
  imageUrl: string | null | undefined;
  imageDescription: string | null | undefined;
};

export const QaStage = ({
  answers,
  backgroundImage,
  categories,
  description,
  keywords,
  linkText,
  linkUrl,
  name,
  qaCategoryText,
  qaFilterText,
  qaInputText,
  qaResetText,
  qaResultPluralText,
  qaResultSingularText,
  title,
  adjacentArticleCategoryId,
  initialShowAnswers,
}: {
  answers: {
    title: string | null;
    pageUrl: string | null;
    productCategoryTitle: string | null | undefined;
    productCategoryId: string | null | undefined;
    searchKeywords: string | null;
    rawContent: string | undefined;
  }[];
  backgroundImage: string | null | undefined;
  categories: QaStageCategory[];
  description: string | null;
  keywords: {
    linkUrl: string | null;
    linkText: string | null;
    keyword: string | null;
    name: string | null;
    ingredient: {
      title: string | null;
      content: React.ReactNode;
    } | null;
  }[];
  linkText: string | null;
  linkUrl: string | null;
  name: string | null;
  qaCategoryText: string | null;
  qaFilterText: string | null;
  qaInputText: string | null;
  qaResetText: string | null;
  qaResultPluralText: string | null;
  qaResultSingularText: string | null;
  title: string | null;
  adjacentArticleCategoryId: string | undefined;
  initialShowAnswers: boolean;
}) => {
  const searchParams = useSearchParams();

  const [stageState, setStageState] = useState<{
    keywords: string;
    categoryId: string | null;
    showAnswers: boolean;
  }>(() => ({
    keywords: searchParams?.get("keywords") ?? "",
    categoryId:
      /**
       * 1. Prefer the category which matches the query-string parameter
       */
      categories.find(
        (item) =>
          item.linkUrl && item.linkUrl === searchParams?.get("category"),
      )?.id ??
      /**
       * 2. Fall-back to the category which matches the adjacent article (if
       * there is one).
       */
      adjacentArticleCategoryId ??
      /**
       * 3. Fall-back to the "default" category.
       */
      categories.find((item) => item.linkUrl?.includes("red-bull-energy-drink"))
        ?.id ??
      null,
    showAnswers: initialShowAnswers,
  }));

  const [isDropdownExpanded, setIsDropdownExpanded] = useState<boolean>(false);

  /**
   * Filter the answers which match the search-keyword(s) or by the currently
   * selected category.
   */
  const activeAnswers = answers.filter((answer) => {
    if (stageState.keywords) {
      const keywordString = stageState.keywords
        .replace(/\./g, "")
        .toLowerCase();

      return Boolean(
        answer.title?.toLowerCase().includes(keywordString) ||
        answer.searchKeywords?.includes(keywordString) ||
        answer.rawContent?.includes(keywordString),
      );
    }

    if (stageState.categoryId) {
      return answer.productCategoryId === stageState.categoryId;
    }

    return false;
  });

  /**
   * Get the category which is currently selected in the dropdown (if any)
   */
  const activeCategory = categories.find(
    (category) => category.id === stageState.categoryId,
  );

  /**
   * Get "keyword" objects which match current search-terms.
   */
  const matchingKeywords = keywords.filter((item) =>
    stageState.keywords
      ?.split(" ")
      .find(
        (keyword) =>
          keyword.length > 2 &&
          item.keyword?.toLowerCase().includes(keyword.toLowerCase()),
      ),
  );

  /**
   * Get the number of cards that will be shown, including:
   * - `CategoryCard` (activeCategory)
   * - `ResultCard` (activeAnswers)
   * - `IngredientCard` (matchingKeywords)
   */
  const resultCount =
    activeAnswers.length + (activeCategory ? 1 : 0) + matchingKeywords.length;

  const [dropdownSelectedIndex, setDropdownSelectedIndex] = useState<number>(0);

  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div
      className={cx("container")}
      data-show-results={
        JSON.stringify(stageState.showAnswers) /* "true" | "false" */
      }
    >
      <Stage
        name={name}
        title={title}
        description={description}
        linkUrl={linkUrl}
        linkText={linkText}
        backgroundImageUrl={backgroundImage}
        filter={
          <div className={cx("filter-container")}>
            <div className={cx("filter")}>
              <QaStageDropdown
                qaCategoryText={qaCategoryText}
                activeCategory={activeCategory}
                categories={categories}
                isExpanded={isDropdownExpanded}
                setIsExpanded={setIsDropdownExpanded}
                selectedIndex={dropdownSelectedIndex}
                setSelectedIndex={setDropdownSelectedIndex}
                setActiveAnswersState={setStageState}
              />
              <div className={cx("search-field")}>
                <CosmosIconSearch
                  className={cx("search-field-icon")}
                  aria-hidden="true"
                />
                <input
                  className={cx("search-field-input")}
                  type="text"
                  placeholder={qaInputText ?? undefined}
                  value={stageState.keywords}
                  onInput={(event) => {
                    const target = event.target;
                    if (target instanceof HTMLInputElement) {
                      setStageState((current) => ({
                        ...current,
                        keywords: target.value,
                        showAnswers: true,
                      }));
                    }
                  }}
                  ref={inputRef}
                />
              </div>
            </div>
          </div>
        }
      />
      <div className={cx("results-container")}>
        <div className={cx("results-controls")}>
          <CosmosText
            className={cx("results-count", {
              "results-count--no-results": resultCount === 0,
            })}
            size="large"
          >
            {`${
              /* Eg. "99" */
              resultCount
            }
            ${
              /* Eg. "Result" | "Results" */
              resultCount === 1 ? qaResultSingularText : qaResultPluralText
            }
            ${
              /* Eg. "– Select Category" (only shown if no results ) */
              resultCount === 0 && !activeCategory && !stageState.keywords
                ? `– ${qaFilterText}`
                : ""
            }`}
          </CosmosText>
          {(stageState.categoryId || stageState.keywords) && (
            <CosmosButton
              icon-placement="before"
              kind="link"
              size="large"
              className={cx("reset")}
              onClick={() => {
                setStageState((current) => ({
                  ...current,
                  keywords: "",
                  categoryId: null,
                }));
              }}
            >
              <CosmosIconReload
                className={cx("reset-icon")}
                slot="icon"
                aria-hidden="true"
              />
              {qaResetText}
            </CosmosButton>
          )}
        </div>
        {(activeCategory || stageState.keywords) && (
          <div>
            {activeCategory && <CategoryCard activeCategory={activeCategory} />}
            {keywords &&
              matchingKeywords.map(
                (item) =>
                  item.ingredient && (
                    <IngredientCard
                      name={item.name}
                      linkText={item.linkText}
                      linkUrl={item.linkUrl}
                      ingredient={item.ingredient}
                    />
                  ),
              )}
            {activeAnswers.map((item, index) => (
              <ResultCard
                key={index}
                title={item.title}
                pageUrl={item.pageUrl}
                productCategoryTitle={item.productCategoryTitle}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function CategoryCard({ activeCategory }: { activeCategory: QaStageCategory }) {
  const imageLoading = useImageLoading();
  return (
    <a
      className={cx("card", "card--category")}
      href={activeCategory.linkUrl ?? undefined}
    >
      <div className={cx("category-content")}>
        <div className={cx("card-header")}>
          <CosmosTitle
            className={cx("section-title")}
            size="x-small"
            tag="span"
          >
            {activeCategory.name}
          </CosmosTitle>
        </div>
        <div>
          <CosmosTitle className={cx("category-title")} tag="h3" size="x-large">
            {activeCategory.title}
          </CosmosTitle>
          {activeCategory.linkUrl && activeCategory.linkText && (
            <CosmosButton
              decorative={true}
              size="large"
              kind="primary"
              appearance="light"
              className={cx("card-button")}
            >
              {activeCategory.linkText}
            </CosmosButton>
          )}
        </div>
      </div>
      {activeCategory.imageUrl && (
        <div className={cx("category-image-wrapper")}>
          <img
            className={cx("category-image")}
            loading={imageLoading}
            srcSet={responsiveImageHelper.createContentfulSrcSet(
              activeCategory.imageUrl,
            )}
            sizes="min(50vw, 396px)"
            alt={activeCategory.imageDescription ?? ""}
          />
        </div>
      )}
    </a>
  );
}

function IngredientCard({
  name,
  linkText,
  linkUrl,
  ingredient,
}: {
  name: string | null;
  linkUrl: string | null;
  linkText: string | null;
  ingredient: {
    title: string | null;
    content: React.ReactNode;
  };
}) {
  return (
    <div className={cx("card")}>
      <div className={cx("card-header")}>
        <CosmosTitle className={cx("section-title")} size="x-small" tag="span">
          {name}
        </CosmosTitle>
      </div>
      <CosmosTitle tag="h3" className={cx("ingredient-title")} size="x-large">
        {ingredient.title}
      </CosmosTitle>
      <CosmosText
        className={cx("ingredient-content")}
        tag={"div" /* content should have <p> tags */}
      >
        {ingredient.content}
      </CosmosText>

      {linkUrl && linkText && (
        <div className={cx("read-more-container")}>
          <CosmosButton
            href={linkUrl}
            kind="link"
            iconPlacement="after"
            className={cx("read-more")}
          >
            <CosmosIconChevronRight
              slot="icon"
              className={cx("read-more-icon")}
              aria-hidden="true"
            />
            {linkText}
          </CosmosButton>
        </div>
      )}
    </div>
  );
}

function ResultCard({
  title,
  pageUrl,
  productCategoryTitle,
}: {
  title: string | null;
  pageUrl: string | null;
  productCategoryTitle: string | null | undefined;
}) {
  return (
    <a className={cx("card")} href={pageUrl ?? undefined}>
      {productCategoryTitle && (
        <div className={cx("card-header")}>
          <CosmosTitle className={cx("section-title")} size="x-small">
            {"Q&A"}
          </CosmosTitle>
          <CosmosTitle
            className={cx("section-title", "section-title--grey")}
            size="x-small"
          >
            {productCategoryTitle}
          </CosmosTitle>
        </div>
      )}
      <CosmosText size="x-large" tag="h3">
        {title}
      </CosmosText>
    </a>
  );
}
